import { useInternational } from '@/common/locale';
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { generateUUID } from '@/common/utils/commonUtils';
import { checkValid } from '@/common/utils/validation';
import { REG_EXP } from '@/common/utils';
import { cloneDeep, debounce, isNil } from 'lodash-es';
import { UrlInfoForUi, useDashboardPopupEnvStore } from '@/common/stores/dashboard-popup';
import { checkRole } from '@/common/auth/auth.utils';
import { DashboardNameItem } from '@/api-client/metav8Over/model';
import { useMessage } from '@/common/inject/useMessage';
import { useMyInfoStore } from '@/config/stores/my-info';
import { useDashboardJumpStore } from '@/dashboard/stores/dashboard-jump';

export interface Props {
  envKey: string;
  isAlert?: boolean;
  isShow: boolean;
}
export interface Emit {
  (e: 'close'): void;
  (e: 'update:isShow', value: boolean): void;
}

export const useWindowHeight = () => {
  const DEFAULT_HEIGHT = {
    WINDOW: 480,
    ROW: 28,
    MAX: 200,
  };
  const windowHeight = ref(`${DEFAULT_HEIGHT.WINDOW}px`);

  const changeWindowHeight = (itemCount: number = 0) => {
    if (!itemCount) {
      windowHeight.value = `${DEFAULT_HEIGHT.WINDOW}px`;
      return;
    }

    const rowsHeight = itemCount * DEFAULT_HEIGHT.ROW + 8;
    const totalHeight = DEFAULT_HEIGHT.WINDOW + Math.min(rowsHeight, DEFAULT_HEIGHT.MAX) + 8;
    windowHeight.value = `${totalHeight}px`;
  };
  return {
    windowHeight,
    changeWindowHeight,
  };
};

export const setup = (props: Props, emit: Emit) => {
  const { myInfo } = storeToRefs(useMyInfoStore());
  const { t } = useInternational();
  const isLoading = ref<boolean>(false);
  const searchValue = ref<string>('');
  const checkedList = ref<number[]>([]);
  const searchFilterList = ref<DashboardNameItem[]>([]);

  const { showSuccessMsg } = useMessage();

  const dashboardJumpStore = useDashboardJumpStore();
  const { dashboardJumpList } = storeToRefs(dashboardJumpStore);
  const { fetchDashboardJumpList } = dashboardJumpStore;

  const dashboardPopupEnvStore = useDashboardPopupEnvStore();
  const { setupDashBoardData } = storeToRefs(dashboardPopupEnvStore);
  const {
    fetchDashboardPopupEnvInfoForKey,
    saveEnvInfoForKey,
    deleteSetupDashboardDataByEnvKey,
    updateSetupDashboardDataByEnvKey,
  } = dashboardPopupEnvStore;

  const urlInfoList = ref<UrlInfoForUi[]>([]);
  const dashboardName = ref<string>('');

  const { windowHeight, changeWindowHeight } = useWindowHeight();

  const getDefaultUrlField = (): UrlInfoForUi => ({
    checked: false,
    url: '',
    urlName: '',
    isNewWindow: false,
  });
  const isMaster = computed(() => {
    const myRole = myInfo.value.role;
    return checkRole.isMaster(myRole);
  });

  const initData = () => {
    const envData = setupDashBoardData.value.get(props.envKey);
    dashboardName.value = envData?.name ?? '';
    urlInfoList.value = cloneDeep(envData?.urlList ?? []);

    checkedList.value =
      envData?.dashboardList?.filter((i) => i.dashboardId).map((v: any) => v.dashboardId) ?? [];
    searchValue.value = '';
    searchFilterList.value = dashboardJumpList.value;
  };

  const nameErrorMsg = ref({ name: '' });

  const isPopupShow = computed<boolean>({
    get: () => props.isShow,
    set: (isShow) => {
      initData();
      emit('update:isShow', isShow);
    },
  });

  const isValid = ref(false);

  const fetchDashboardPopup = async () => {
    deleteSetupDashboardDataByEnvKey(props.envKey);
    await fetchDashboardJumpList();
    await fetchDashboardPopupEnvInfoForKey(props.envKey);
    initData();
  };

  const onClickSave = async () => {
    dashboardName.value = dashboardName.value.trim();
    const updatedDashboardList = dashboardJumpList.value.filter(
      ({ dashboardId }) => !isNil(dashboardId) && checkedList.value.includes(dashboardId),
    );

    const updatedUrlList = urlInfoList.value.map((v, idx) => {
      return {
        checked: v.checked,
        url: v.url,
        urlName: v.urlName,
        index: idx,
        isGlobal: v.isGlobal ? v.isGlobal : isMaster.value,
        id: v.id ? v.id : generateUUID(),
        isNewWindow: v.isNewWindow ?? false,
      };
    });

    const updatedSetupDashBoardDataByEnvKey = {
      name: dashboardName.value,
      checked: false,
      dashboardList: updatedDashboardList,
      urlList: updatedUrlList,
    };

    updateSetupDashboardDataByEnvKey(props.envKey, updatedSetupDashBoardDataByEnvKey);

    const setupDashBoardByEnvKey = JSON.stringify(updatedSetupDashBoardDataByEnvKey);
    await saveEnvInfoForKey(props.envKey, setupDashBoardByEnvKey);
    showSuccessMsg({ msgStr: t('MESSAGE.SAVED') });
    isPopupShow.value = false;
    emit('close');
  };
  const validateName = (value: string | number) => {
    if (!checkValid(REG_EXP.BUTTON_NAME, `${value}`)) {
      nameErrorMsg.value.name = t('NOTI.VALIDATION.INVALID_BUTTON_NAME');
    } else {
      nameErrorMsg.value.name = '';
    }
  };
  const validateUrl = (value, idx) => {
    if (value.trim() === '') {
      urlInfoList.value[idx].urlErrorMsg = t('MESSAGE.REQUIRE_DATA');
    } else {
      urlInfoList.value[idx].urlErrorMsg = '';
    }
  };
  const validateUrlName = (value, idx) => {
    if (value.trim() === '') {
      urlInfoList.value[idx].nameErrorMsg = t('MESSAGE.REQUIRE_DATA');
    } else {
      urlInfoList.value[idx].nameErrorMsg = '';
    }
  };

  const onClickAddBtn = () => {
    urlInfoList.value.push(getDefaultUrlField());
    isValid.value = false;
  };

  const onClickDeleteBtn = (idx: number) => {
    urlInfoList.value.splice(idx, 1);
  };

  watch(
    searchValue,
    debounce(() => {
      searchFilterList.value = dashboardJumpList.value.filter((v) =>
        v.dashboardName?.toLowerCase().includes(searchValue.value.toLowerCase()),
      );
    }, 300),
  );

  const updateIsValid = () => {
    const hasError = nameErrorMsg.value.name !== '';

    const hasUrlError = urlInfoList.value.some((v) => v.nameErrorMsg || v.urlErrorMsg);
    const isEmpty = urlInfoList.value.some(
      (item) => item.url.trim() === '' || item.urlName.trim() === '',
    );

    isValid.value = !(hasUrlError || hasError || isEmpty);
  };

  watch(
    [() => urlInfoList.value, nameErrorMsg],
    () => {
      updateIsValid();

      changeWindowHeight(urlInfoList.value?.length);
    },
    { deep: true },
  );

  watch(
    () => dashboardName.value,
    () => {
      updateIsValid();
    },
  );

  onMounted(async () => {
    if (!props.envKey) return;

    isLoading.value = true;

    await fetchDashboardPopup();

    isLoading.value = false;
    searchFilterList.value = dashboardJumpList.value;
  });

  return {
    isPopupShow,
    urlInfoList,
    dashboardName,
    onClickSave,
    isValid,

    searchValue,
    nameErrorMsg,
    validateName,
    validateUrl,
    validateUrlName,
    checkedList,
    isLoading,
    t,
    onClickAddBtn,
    onClickDeleteBtn,
    isMaster,

    windowHeight,
    searchFilterList,
  };
};
